import { useEffect, useRef, useState } from 'react';
import {
  Trash2,
  Pencil,
  X,
  AlertCircle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react';
import { issueAnalysisApi, EntityTypeDefinition } from '../../services/issueAnalysisApi';

const PAGE_SIZE = 20;

export default function EntityTypeEditor() {
  const [rows, setRows] = useState<EntityTypeDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState<string | null>(null);

  // Pagination & search
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Edit mode
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formKey, setFormKey] = useState(0);
  const formRef = useRef<HTMLFormElement | null>(null);

  const editing = editingId !== null
    ? rows.find((r) => r.id === editingId)
    : undefined;

  // Filter rows by search query
  const filteredRows = rows.filter(
    (r) =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.aliases && r.aliases.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const activeRows = filteredRows.filter((r) => !r.disabled_at);
  const totalPages = Math.ceil(activeRows.length / PAGE_SIZE);
  const paginatedRows = activeRows.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE
  );

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await issueAnalysisApi.listEntityTypes();
      setRows(res.entity_types);
      setCurrentPage(0);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const startEdit = (row: EntityTypeDefinition) => {
    setEditingId(row.id);
    setSaveError(null);
    setFormKey((k) => k + 1);
    window.setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setSaveError(null);
    setFormKey((k) => k + 1);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;

    const fd = new FormData(formRef.current);
    const name = (fd.get('entity_type_name') as string | null)?.trim() ?? '';
    if (!name) {
      setSaveError('Name is required.');
      return;
    }
    const tier = parseInt((fd.get('entity_type_tier') as string) || '1', 10);
    const match_op = ((fd.get('entity_type_match_op') as string) || 'eq') as 'eq' | 'like';
    const queryable = fd.get('entity_type_queryable') === 'on';
    const aliases = ((fd.get('entity_type_aliases') as string) || '').trim();
    const column_candidates = ((fd.get('entity_type_column_candidates') as string) || '').trim();
    const description = ((fd.get('entity_type_description') as string) || '').trim();

    setSaving(true);
    setSaveError(null);
    try {
      const editingIdAtStart = editingId;
      if (editingIdAtStart !== null) {
        await issueAnalysisApi.deleteEntityType(editingIdAtStart);
      }
      await issueAnalysisApi.createEntityType({
        name,
        aliases: aliases || undefined,
        tier,
        match_op,
        queryable,
        column_candidates: column_candidates || undefined,
        description: description || undefined,
      });
      setEditingId(null);
      setFormKey((k) => k + 1);
      setSavedToast(
        editingIdAtStart !== null
          ? `Updated "${name}".`
          : `Added "${name}".`,
      );
      window.setTimeout(() => setSavedToast(null), 3500);
      await refresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (row: EntityTypeDefinition) => {
    if (!window.confirm(`Disable "${row.name}"?`)) return;
    try {
      await issueAnalysisApi.deleteEntityType(row.id);
      if (editingId === row.id) {
        cancelEdit();
      } else {
        await refresh();
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">Entity Types</h3>
        <p className="text-sm text-gray-600">
          Add new entity types that the bot should recognise. Types added
          here appear in the "Tag as Entity Type" dropdown inside the AI
          Summary correction menu and take effect on the next ticket.
          Entity types map JIRA ticket entities to modules, tables, and fields.
        </p>
      </div>

      {/* Form */}
      <form
        ref={formRef}
        key={formKey}
        onSubmit={handleSubmit}
        autoComplete="off"
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore
        className="border border-gray-200 rounded-lg p-4 bg-gray-50/50 space-y-3"
      >
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">
            {editing ? `Edit entity type: ${editing.name}` : 'Add new entity type'}
          </h4>
          {editing && (
            <button
              type="button"
              onClick={cancelEdit}
              className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
            >
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            name="entity_type_name"
            type="text"
            required
            defaultValue={editing?.name ?? ''}
            placeholder="Transportation Dispatch Notice"
            autoComplete="off"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore=""
            style={{ color: '#111', WebkitTextFillColor: '#111' }}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 bg-white"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Aliases (comma-separated)
          </label>
          <input
            name="entity_type_aliases"
            type="text"
            defaultValue={editing?.aliases ?? ''}
            placeholder="tdn, dispatchnotice"
            autoComplete="off"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore=""
            style={{ color: '#111', WebkitTextFillColor: '#111' }}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 bg-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Tier</label>
            <select
              name="entity_type_tier"
              defaultValue={String(editing?.tier ?? 1)}
              style={{ color: '#111', WebkitTextFillColor: '#111' }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-900"
            >
              <option value="1">1 — Unique identifier</option>
              <option value="2">2 — Compound navigation</option>
              <option value="3">3 — Contextual</option>
              <option value="4">4 — Drop silently</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Match operator</label>
            <select
              name="entity_type_match_op"
              defaultValue={editing?.match_op ?? 'eq'}
              style={{ color: '#111', WebkitTextFillColor: '#111' }}
              className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-900"
            >
              <option value="eq">eq (exact match)</option>
              <option value="like">like (substring match)</option>
            </select>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <input
            id="entity-type-queryable"
            name="entity_type_queryable"
            type="checkbox"
            defaultChecked={editing ? editing.queryable : true}
            className="mt-0.5"
          />
          <label htmlFor="entity-type-queryable" className="text-sm text-gray-700">
            <strong>Queryable</strong> — values of this type appear in SQL WHERE clauses.
            Uncheck for label-only types that should never be queried.
          </label>
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Column candidates (comma-separated)
          </label>
          <input
            name="entity_type_column_candidates"
            type="text"
            defaultValue={editing?.column_candidates ?? ''}
            placeholder="tdnno, dispatchno, dispatchnoticeno"
            autoComplete="off"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore=""
            style={{ color: '#111', WebkitTextFillColor: '#111' }}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 bg-white"
          />
        </div>

        <div>
          <label className="block text-sm text-gray-700 mb-1">Description</label>
          <textarea
            name="entity_type_description"
            rows={2}
            defaultValue={editing?.description ?? ''}
            autoComplete="off"
            spellCheck={false}
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore=""
            style={{ color: '#111', WebkitTextFillColor: '#111' }}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm text-gray-900 bg-white"
          />
        </div>

        {saveError && (
          <div className="flex items-start gap-1 text-sm text-red-600">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : editing ? 'Update Entity Type' : 'Save Entity Type'}
          </button>
        </div>
      </form>

      {/* Search & List */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Search size={16} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search entity types..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(0);
            }}
            className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
          />
        </div>

        <h4 className="text-sm font-semibold text-gray-800 mb-3">
          Active entity types ({activeRows.length})
        </h4>

        {loading && <p className="text-sm text-gray-500">Loading...</p>}
        {loadError && (
          <div className="text-sm text-red-600 flex items-start gap-1">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>Failed to load: {loadError}</span>
          </div>
        )}
        {!loading && !loadError && activeRows.length === 0 && (
          <p className="text-sm text-gray-500">
            {searchQuery ? 'No entity types match your search.' : 'No entity types defined yet.'}
          </p>
        )}
        {!loading && !loadError && activeRows.length > 0 && (
          <>
            <div className="space-y-2 mb-4">
              {paginatedRows.map((r) => (
                <div
                  key={r.id}
                  className={`border border-gray-200 rounded-lg p-3 flex items-start gap-3 ${
                    editingId === r.id ? 'bg-blue-50 border-blue-300' : 'bg-white'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 break-words">{r.name}</div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                      <span>Tier {r.tier}</span>
                      <span>Match: {r.match_op}</span>
                      <span>Queryable: {r.queryable ? 'Yes' : 'No'}</span>
                    </div>
                    {r.aliases && (
                      <div className="text-xs text-gray-500 mt-1 break-words">
                        <span className="font-medium">Aliases:</span> {r.aliases}
                      </div>
                    )}
                    {r.column_candidates && (
                      <div className="text-xs text-gray-500 mt-0.5 break-words">
                        <span className="font-medium">Columns:</span> {r.column_candidates}
                      </div>
                    )}
                    {r.description && (
                      <div className="text-xs text-gray-500 mt-0.5 break-words">{r.description}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => startEdit(r)}
                      className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1"
                      title="Edit this entity type"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDisable(r)}
                      className="text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded flex items-center gap-1"
                      title="Delete this entity type"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div>
                  Page {currentPage + 1} of {totalPages} ({activeRows.length} total)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage === totalPages - 1}
                    className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {savedToast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-50">
          <CheckCircle size={14} />
          {savedToast}
        </div>
      )}
    </div>
  );
}
